import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator, LayoutChangeEvent } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography, withAlpha } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useLocale } from '../../contexts/LocaleContext';
import { useOutreachSummary } from '../../hooks/useOutreachSummary';
import { apiService, HomeDashboardData, HomeAgendaItem, BillingOverview } from '../../services/api';
import { AreaChartCard } from '../../screens/dashboard/AreaChartCard';
import { formatReportDuration } from '../../utils/reportFormat';
import { Screen } from '../../contexts/AppContext';
import type { RecentPost } from '../../screens/dashboard/RecentPostsCard';
import { PagePostsList } from './PagePostsList';
import { AiActivityCard } from './AiActivityCard';
import { FlowRunsByContactCard } from './FlowRunsByContactCard';
import { AppIcon } from '../icons/AppIcon';

/**
 * Home: each block answers one question — what needs me now (attention),
 * how are we doing (KPIs for the picked range), what's left today
 * (agenda), where is it heading (one trend chart), how's the team (top
 * agents, managers/admins only). Blocks with nothing to say don't render.
 * Numbers come from GET /api/reports/home (same source as Reports) plus
 * the shared outreach summary.
 */

type RangeKey = 'today' | 'week' | 'month';
type TrendKey = 'messages' | 'calls' | 'visits';

export interface HomeShortcut {
  id: string;
  title: string;
  icon: string;
  color: string;
}

export interface HomeDashboardProps {
  /** Bumped by the parent's pull-to-refresh to reload everything. */
  refreshToken: number;
  onNavigate: (screen: Screen, param?: string, routeParams?: Record<string, any>) => void;
  shortcuts: HomeShortcut[];
  onShortcutPress: (id: string) => void;
  /** Setup progress 0-100; the banner shows only below 100. */
  setupPercentage?: number;
  /** Line under the title, e.g. "3 of 9 done · Next: Add products". */
  setupDetail?: string;
  onOpenSetup: () => void;
  /** Shows a Hide button on the banner. */
  onDismissSetup?: () => void;
  /** Latest posts of the connected Facebook page; null/undefined = no page connected. */
  pagePosts?: { posts: RecentPost[]; pageId?: string; canCreate: boolean } | null;
  initialRange?: RangeKey;
}

const RANGE_DAYS: Record<RangeKey, number> = { today: 1, week: 7, month: 30 };
const WIDE_BREAKPOINT = 900;

const rangeDates = (range: RangeKey) => {
  const dayStart = new Date();
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(dayStart);
  dayEnd.setHours(23, 59, 59, 999);
  const startDate = new Date(dayStart);
  startDate.setDate(startDate.getDate() - (RANGE_DAYS[range] - 1));
  return { startDate, endDate: dayEnd, dayStart, dayEnd };
};

const formatTime = (iso: string, locale: string) => {
  try {
    return new Date(iso).toLocaleTimeString(locale === 'ar' ? 'ar' : 'en', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

const formatNumber = (n: number, locale: string) => {
  try {
    return n.toLocaleString(locale === 'ar' ? 'ar' : 'en', { maximumFractionDigits: 0 });
  } catch {
    return String(n);
  }
};

export const HomeDashboard: React.FC<HomeDashboardProps> = ({ refreshToken, onNavigate, shortcuts, onShortcutPress, setupPercentage, setupDetail, onOpenSetup, onDismissSetup, pagePosts, initialRange = 'today' }) => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const { t, locale } = useLocale();
  const { data: outreach, refresh: refreshOutreach } = useOutreachSummary();

  const [range, setRange] = useState<RangeKey>(initialRange);
  const [trendKey, setTrendKey] = useState<TrendKey>('messages');
  const [data, setData] = useState<HomeDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [width, setWidth] = useState(0);
  const [billing, setBilling] = useState<BillingOverview | null>(null);

  useEffect(() => {
    apiService.getBillingOverview()
      .then((res) => { if (res.statusCode === 1000 && res.data) setBilling(res.data); })
      .catch(() => { /* banner is optional */ });
  }, [refreshToken]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getHomeDashboard(rangeDates(range));
      if (res.statusCode === 1000 && res.data) {
        setData(res.data);
        setFailed(false);
      } else {
        setFailed(true);
      }
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load, refreshToken]);

  useEffect(() => {
    if (refreshToken > 0) refreshOutreach?.();
  }, [refreshToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const durationUnits = useMemo(() => ({
    s: t('reports.duration.s'), m: t('reports.duration.m'), h: t('reports.duration.h'), d: t('reports.duration.d'),
  }), [t]);

  const isWide = width >= WIDE_BREAKPOINT;
  const onLayout = (e: LayoutChangeEvent) => setWidth(e.nativeEvent.layout.width);

  // ─── Attention ──────────────────────────────────────────────────────────
  const attentionItems = useMemo(() => {
    const a = data?.attention;
    const items: { key: string; count: number; icon: string; color: string; onPress: () => void }[] = [
      { key: 'needsResponse', count: outreach?.needsResponse.count ?? 0, icon: 'chatbox-ellipses-outline', color: colors.status.error as string, onPress: () => onNavigate('contacts', undefined, { initialFilter: 'needsResponse' }) },
      { key: 'expiringSoon', count: outreach?.expiringSoon?.count ?? 0, icon: 'alarm-outline', color: colors.status.error as string, onPress: () => onNavigate('contacts', undefined, { initialFilter: 'expiringSoon' }) },
      { key: 'overdueAssignments', count: a?.overdueAssignments ?? 0, icon: 'alert-circle-outline', color: colors.status.error as string, onPress: () => onNavigate('dashboard-assignments-all') },
      { key: 'visitsOverdue', count: a?.visitsOverdue ?? 0, icon: 'walk-outline', color: colors.status.error as string, onPress: () => onNavigate('visits') },
      { key: 'followUpDue', count: outreach?.followUpDue.count ?? 0, icon: 'time-outline', color: colors.status.warning as string, onPress: () => onNavigate('contacts', undefined, { initialFilter: 'followUpDue' }) },
      { key: 'expiringQuotations', count: a?.expiringQuotations ?? 0, icon: 'document-text-outline', color: colors.status.warning as string, onPress: () => onNavigate('quotations') },
      { key: 'pendingOrders', count: a?.pendingOrders ?? 0, icon: 'receipt-outline', color: colors.status.warning as string, onPress: () => onNavigate('orders') },
      { key: 'visitsToday', count: a?.visitsToday ?? 0, icon: 'navigate-outline', color: colors.status.info as string, onPress: () => onNavigate('visits') },
      { key: 'eventsToday', count: a?.eventsToday ?? 0, icon: 'calendar-outline', color: colors.status.info as string, onPress: () => onNavigate('business-calendar') },
    ];
    return items.filter((i) => i.count > 0);
  }, [data, outreach, onNavigate]);

  // ─── KPIs ───────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const k = data?.kpis;
    if (!k) return [];
    const tiles: { key: string; icon: string; label: string; value: string; detail?: string; onPress?: () => void }[] = [
      { key: 'messages', icon: 'chatbubbles-outline', label: t('home.kpi.messages'), value: formatNumber(k.messages.sent, locale), detail: t('home.kpi.received', { count: formatNumber(k.messages.received, locale) }), onPress: () => onNavigate('performance') },
      { key: 'reached', icon: 'people-outline', label: t('home.kpi.reached'), value: formatNumber(k.customersReached, locale), onPress: () => onNavigate('performance') },
      {
        key: 'calls', icon: 'call-outline', label: t('home.kpi.calls'), value: formatNumber(k.calls.total, locale),
        detail: t('home.kpi.callsDetail', { answered: formatNumber(k.calls.answered, locale), talk: formatReportDuration(k.calls.talkTimeSeconds, durationUnits, { compact: true }) }),
        onPress: () => onNavigate('performance'),
      },
    ];
    if (k.visits) {
      tiles.push({
        key: 'visits', icon: 'walk-outline', label: t('home.kpi.visits'), value: formatNumber(k.visits.total, locale),
        detail: t('home.kpi.visitsDetail', { completed: formatNumber(k.visits.completed, locale), open: formatNumber(k.visits.open, locale) }),
        onPress: () => onNavigate('visits'),
      });
    }
    if (k.orders) {
      const top = k.orders.revenue[0];
      tiles.push({
        key: 'orders', icon: 'receipt-outline', label: t('home.kpi.orders'), value: formatNumber(k.orders.count, locale),
        detail: top ? `${top.currency} ${formatNumber(top.total, locale)}${k.orders.revenue.length > 1 ? ' +' : ''}` : undefined,
        onPress: () => onNavigate('orders'),
      });
    }
    if (k.quotations) {
      tiles.push({
        key: 'quotations', icon: 'document-text-outline', label: t('home.kpi.quotations'), value: formatNumber(k.quotations.created, locale),
        detail: t('home.kpi.quotationsDetail', { accepted: formatNumber(k.quotations.accepted, locale) }),
        onPress: () => onNavigate('quotations'),
      });
    }
    return tiles;
  }, [data, t, locale, durationUnits, onNavigate]);

  // ─── Trend ──────────────────────────────────────────────────────────────
  const trendOptions: TrendKey[] = useMemo(() => {
    const opts: TrendKey[] = ['messages', 'calls'];
    if (data?.kpis.visits) opts.push('visits');
    return opts;
  }, [data]);
  const trendData = data?.trend[trendKey] ?? [];
  const showTrend = range !== 'today' && trendData.some((p) => p.count > 0);

  // ─── Render helpers ─────────────────────────────────────────────────────
  const sectionTitle = (title: string, action?: { label: string; onPress: () => void }) => (
    <View style={[styles.sectionHeader, { flexDirection: rtl.rowDirection }]}>
      <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{title}</Text>
      {action && (
        <TouchableOpacity onPress={action.onPress} hitSlop={8}>
          <Text style={styles.sectionAction}>{action.label}</Text>
        </TouchableOpacity>
      )}
    </View>
  );

  const rangeSwitch = (
    <View style={[styles.segmented, { flexDirection: rtl.rowDirection }]}>
      {(['today', 'week', 'month'] as RangeKey[]).map((key) => {
        const active = key === range;
        return (
          <TouchableOpacity key={key} style={[styles.segment, active && styles.segmentActive]} onPress={() => setRange(key)} accessibilityState={{ selected: active }}>
            <Text style={[styles.segmentText, active && styles.segmentTextActive]}>{t(`home.range.${key}` as any)}</Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );

  const setupBanner = setupPercentage !== undefined && setupPercentage < 100 ? (
    <TouchableOpacity style={[styles.setupBanner, { flexDirection: rtl.rowDirection }]} onPress={onOpenSetup} activeOpacity={0.85}>
      <Ionicons name="rocket-outline" size={18} color={colors.primary as string} />
      <View style={styles.setupBody}>
        <Text style={[styles.setupTitle, { textAlign: rtl.textStart }]} numberOfLines={1}>{t('home.setup.title')}</Text>
        {!!setupDetail && <Text style={[styles.setupDetail, { textAlign: rtl.textStart }]} numberOfLines={1}>{setupDetail}</Text>}
        <View style={styles.progressTrack}>
          <View style={[styles.progressFill, { width: `${Math.max(4, setupPercentage)}%` }]} />
        </View>
      </View>
      <Text style={styles.setupPercent}>{setupPercentage}%</Text>
      {onDismissSetup ? (
        <TouchableOpacity onPress={onDismissSetup} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }} accessibilityRole="button" accessibilityLabel={t('setup.dismiss')}>
          <Ionicons name="close" size={16} color={colors.text.secondary as string} />
        </TouchableOpacity>
      ) : (
        <Ionicons name={rtl.mirrorIcon('chevron-forward') as any} size={16} color={colors.text.secondary as string} />
      )}
    </TouchableOpacity>
  ) : null;

  // Owners/admins only: the plan needs action soon (trial ending, grace,
  // paused). Each business is billed separately — this is the current one.
  const billingBanner = (() => {
    if (!billing?.canManage) return null;
    let message: string | null = null;
    let tone = colors.status.warning as string;
    if (billing.state === 'grace') message = t('home.billing.grace', { days: billing.graceDaysLeft || 0 });
    else if (billing.state === 'expired') { message = t('home.billing.ended'); tone = colors.status.error as string; }
    else if (billing.state === 'trial' && billing.daysLeft <= 3) message = t('home.billing.trialEnding', { days: billing.daysLeft });
    else if (billing.state === 'none') message = t('home.billing.noPlan');
    if (!message) return null;
    return (
      <TouchableOpacity
        style={[styles.billingBanner, { flexDirection: rtl.rowDirection, borderColor: withAlpha(tone, 0.4), backgroundColor: withAlpha(tone, 0.08) }]}
        onPress={() => onNavigate(billing.state === 'none' ? 'plan-selection' : 'subscription')}
        activeOpacity={0.85}
      >
        <Ionicons name="card-outline" size={18} color={tone} />
        <Text style={[styles.billingBannerText, { textAlign: rtl.textStart }]}>{message}</Text>
        <Text style={[styles.billingBannerAction, { color: tone }]}>{t('home.billing.action')}</Text>
      </TouchableOpacity>
    );
  })();

  const attentionBlock = (
    <View style={styles.section}>
      {sectionTitle(t('home.attention.title'))}
      {attentionItems.length === 0 ? (
        <View style={[styles.allClear, { flexDirection: rtl.rowDirection }]}>
          <Ionicons name="checkmark-circle" size={18} color={colors.status.success as string} />
          <Text style={[styles.allClearText, { textAlign: rtl.textStart }]}>{t('home.attention.allClear')}</Text>
        </View>
      ) : (
        <View style={[styles.chipGrid, { flexDirection: rtl.rowDirection }]}>
          {attentionItems.map((item) => (
            <TouchableOpacity key={item.key} style={[styles.attentionChip, isWide && styles.attentionChipWide, { flexDirection: rtl.rowDirection, borderColor: withAlpha(item.color, 0.35) }]} onPress={item.onPress} activeOpacity={0.8}>
              <View style={[styles.attentionIcon, { backgroundColor: withAlpha(item.color, 0.12) }]}>
                <AppIcon name={item.icon} size={16} color={item.color} />
              </View>
              <Text style={[styles.attentionCount, { color: item.color }]}>{formatNumber(item.count, locale)}</Text>
              <Text style={[styles.attentionLabel, { textAlign: rtl.textStart }]} numberOfLines={2}>{t(`home.attention.${item.key}` as any)}</Text>
            </TouchableOpacity>
          ))}
        </View>
      )}
    </View>
  );

  const kpiBlock = (
    <View style={styles.section}>
      {sectionTitle(t('home.kpi.title'))}
      <View style={[styles.kpiGrid, { flexDirection: rtl.rowDirection }]}>
        {kpis.map((k) => (
          <TouchableOpacity key={k.key} style={[styles.kpiTile, isWide && styles.kpiTileWide]} onPress={k.onPress} disabled={!k.onPress} activeOpacity={0.85}>
            <View style={[styles.kpiHead, { flexDirection: rtl.rowDirection }]}>
              <Ionicons name={k.icon as any} size={15} color={colors.primary as string} />
              <Text style={[styles.kpiLabel, { textAlign: rtl.textStart }]} numberOfLines={2}>{k.label}</Text>
            </View>
            <Text style={[styles.kpiValue, { textAlign: rtl.textStart }]} numberOfLines={1}>{k.value}</Text>
            {!!k.detail && <Text style={[styles.kpiDetail, { textAlign: rtl.textStart }]} numberOfLines={2}>{k.detail}</Text>}
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  const agendaRow = (item: HomeAgendaItem) => (
    <TouchableOpacity
      key={`${item.kind}-${item.id}`}
      style={[styles.agendaRow, { flexDirection: rtl.rowDirection }]}
      onPress={() => (item.kind === 'visit' ? onNavigate('visit-detail', undefined, { visitId: item.id }) : onNavigate('event-detail', item.id))}
      activeOpacity={0.8}
    >
      <Text style={styles.agendaTime}>{formatTime(item.at, locale)}</Text>
      <View style={[styles.agendaIcon, { backgroundColor: withAlpha(item.kind === 'visit' ? colors.status.info : colors.primary, 0.12) }]}>
        <Ionicons name={item.kind === 'visit' ? 'walk-outline' : 'calendar-outline'} size={14} color={(item.kind === 'visit' ? colors.status.info : colors.primary) as string} />
      </View>
      <View style={styles.agendaBody}>
        <Text style={[styles.agendaTitle, { textAlign: rtl.textStart }]} numberOfLines={1}>
          {item.title || t(item.kind === 'visit' ? 'home.agenda.visit' : 'home.agenda.event')}
        </Text>
        {!!item.subtitle && <Text style={[styles.agendaSubtitle, { textAlign: rtl.textStart }]} numberOfLines={1}>{item.subtitle}</Text>}
      </View>
      <Ionicons name={rtl.mirrorIcon('chevron-forward') as any} size={16} color={colors.text.secondary as string} />
    </TouchableOpacity>
  );

  const agendaBlock = (
    <View style={styles.section}>
      {sectionTitle(t('home.agenda.title'))}
      <View style={styles.card}>
        {(data?.agenda.length ?? 0) === 0 ? (
          <Text style={[styles.emptyText, { textAlign: rtl.textStart }]}>{t('home.agenda.empty')}</Text>
        ) : (
          data!.agenda.map(agendaRow)
        )}
      </View>
    </View>
  );

  const trendBlock = showTrend ? (
    <View style={styles.section}>
      <View style={[styles.sectionHeader, { flexDirection: rtl.rowDirection }]}>
        <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t('home.trend.title')}</Text>
        <View style={[styles.segmentedSmall, { flexDirection: rtl.rowDirection }]}>
          {trendOptions.map((key) => (
            <TouchableOpacity key={key} style={[styles.segmentSmall, trendKey === key && styles.segmentActive]} onPress={() => setTrendKey(key)}>
              <Text style={[styles.segmentTextSmall, trendKey === key && styles.segmentTextActive]}>{t(`home.trend.${key}` as any)}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
      <View style={styles.card}>
        <AreaChartCard data={trendData} height={160} />
      </View>
    </View>
  ) : null;

  const teamBlock = data?.scope.team && data.team.length > 0 ? (
    <View style={styles.section}>
      {sectionTitle(t('home.team.title'), { label: t('home.team.viewReports'), onPress: () => onNavigate('performance') })}
      <View style={styles.card}>
        {data.team.map((row, index) => (
          <View key={row.userId} style={[styles.teamRow, { flexDirection: rtl.rowDirection }, index > 0 && styles.rowDivider]}>
            <View style={styles.teamAvatar}>
              <Text style={styles.teamInitial}>{(row.name || '?').trim().charAt(0).toUpperCase()}</Text>
            </View>
            <View style={styles.agendaBody}>
              <Text style={[styles.agendaTitle, { textAlign: rtl.textStart }]} numberOfLines={1}>{row.name || '—'}</Text>
              <Text style={[styles.agendaSubtitle, { textAlign: rtl.textStart }]} numberOfLines={1}>
                {t('home.team.stats', { messages: formatNumber(row.messages, locale), calls: formatNumber(row.calls, locale), visits: formatNumber(row.visits, locale) })}
              </Text>
            </View>
            {row.talkTimeSeconds > 0 && (
              <Text style={styles.teamTalk}>{formatReportDuration(row.talkTimeSeconds, durationUnits, { compact: true })}</Text>
            )}
          </View>
        ))}
      </View>
    </View>
  ) : null;

  const postsBlock = pagePosts ? (
    <View style={styles.section}>
      {sectionTitle(
        t('home.posts.title'),
        pagePosts.canCreate ? { label: t('home.posts.create'), onPress: () => onNavigate('post-create') } : undefined
      )}
      <PagePostsList
        posts={pagePosts.posts}
        onViewAll={() => onNavigate('posts-list')}
        onOpen={(post) => onNavigate('posts-list', undefined, { postId: post.postId, status: 'published' })}
      />
    </View>
  ) : null;

  const shortcutsBlock = shortcuts.length > 0 ? (
    <View style={styles.section}>
      {sectionTitle(t('home.shortcuts.title'))}
      <View style={[styles.shortcutGrid, { flexDirection: rtl.rowDirection }]}>
        {shortcuts.map((s) => (
          <TouchableOpacity key={s.id} style={[styles.shortcut, isWide && styles.shortcutWide]} onPress={() => onShortcutPress(s.id)} activeOpacity={0.8}>
            <View style={[styles.shortcutIcon, { backgroundColor: withAlpha(s.color, 0.12) }]}>
              <AppIcon name={s.icon} size={18} color={s.color} />
            </View>
            <Text style={styles.shortcutLabel} numberOfLines={2}>{s.title}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  ) : null;

  const firstLoad = loading && !data;
  const rangeStart = rangeDates(range).startDate;
  const aiBlock = <AiActivityCard from={rangeStart} refreshToken={refreshToken} title={(text) => sectionTitle(text)} onNavigate={onNavigate} />;
  const runsBlock = <FlowRunsByContactCard from={rangeStart} refreshToken={refreshToken} title={sectionTitle} onNavigate={onNavigate} />;

  return (
    <View style={styles.root} onLayout={onLayout}>
      <View style={[styles.topRow, { flexDirection: rtl.rowDirection }]}>
        {rangeSwitch}
        {loading && !!data && <ActivityIndicator size="small" color={colors.primary as string} />}
      </View>
      {billingBanner}
      {setupBanner}
      {failed && !data ? (
        <Text style={[styles.errorText, { textAlign: rtl.textStart }]}>{t('home.loadError')}</Text>
      ) : firstLoad ? (
        <View style={styles.loading}><ActivityIndicator color={colors.primary as string} /></View>
      ) : isWide ? (
        <View style={[styles.columns, { flexDirection: rtl.rowDirection }]}>
          {/* Balanced: the wide column takes the shortcut grid (6 a row),
              the side column the lists. */}
          <View style={styles.mainColumn}>
            {attentionBlock}
            {kpiBlock}
            {trendBlock}
            {shortcutsBlock}
          </View>
          <View style={styles.sideColumn}>
            {agendaBlock}
            {aiBlock}
            {runsBlock}
            {postsBlock}
            {teamBlock}
          </View>
        </View>
      ) : (
        <>
          {attentionBlock}
          {kpiBlock}
          {agendaBlock}
          {aiBlock}
          {runsBlock}
          {trendBlock}
          {postsBlock}
          {teamBlock}
          {shortcutsBlock}
        </>
      )}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  root: { paddingHorizontal: spacing.md, paddingTop: spacing.sm, paddingBottom: spacing.xl },
  topRow: { alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm, marginBottom: spacing.sm },
  columns: { gap: spacing.lg, alignItems: 'flex-start' },
  mainColumn: { flex: 3, minWidth: 0 },
  sideColumn: { flex: 2, minWidth: 0 },
  loading: { paddingVertical: spacing.xl, alignItems: 'center' },
  errorText: { color: colors.status.error, fontFamily: typography.fontFamily.regular, fontSize: typography.fontSize.sm, paddingVertical: spacing.md },

  section: { marginTop: spacing.md },
  sectionHeader: { alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.sm, gap: spacing.sm },
  sectionTitle: { flexShrink: 1, fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  sectionAction: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.primary },
  card: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
  },
  emptyText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.secondary, paddingVertical: spacing.sm },

  segmented: { backgroundColor: colors.background.secondary, borderRadius: 10, padding: 3, gap: 2 },
  segment: { paddingVertical: 6, paddingHorizontal: spacing.md, borderRadius: 8 },
  segmentActive: { backgroundColor: colors.background.surface, ...designTokens.shadows.sm },
  segmentText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.medium, color: colors.text.secondary },
  segmentTextActive: { color: colors.text.primary, fontFamily: typography.fontFamily.semibold },
  segmentedSmall: { backgroundColor: colors.background.secondary, borderRadius: 8, padding: 2, gap: 2 },
  segmentSmall: { paddingVertical: 4, paddingHorizontal: spacing.sm, borderRadius: 6 },
  segmentTextSmall: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.medium, color: colors.text.secondary },

  setupBanner: {
    alignItems: 'center',
    gap: spacing.sm,
    backgroundColor: withAlpha(colors.primary, 0.08),
    borderWidth: 1,
    borderColor: withAlpha(colors.primary, 0.25),
    borderRadius: designTokens.borderRadius.md,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
  },
  billingBanner: {
    alignItems: 'center',
    gap: spacing.sm,
    borderWidth: 1,
    borderRadius: designTokens.borderRadius.md,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginBottom: spacing.sm,
  },
  billingBannerText: { flex: 1, fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.medium, color: colors.text.primary },
  billingBannerAction: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold },
  setupBody: { flex: 1, minWidth: 0, gap: 6 },
  setupTitle: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  progressTrack: { height: 4, borderRadius: 2, backgroundColor: withAlpha(colors.primary, 0.15), overflow: 'hidden' },
  progressFill: { height: 4, borderRadius: 2, backgroundColor: colors.primary },
  setupDetail: { fontSize: typography.fontSize.xs, color: colors.text.secondary },
  setupPercent: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.bold, color: colors.primary },

  allClear: {
    alignItems: 'center',
    gap: spacing.sm,
    backgroundColor: withAlpha(colors.status.success, 0.08),
    borderRadius: designTokens.borderRadius.md,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
  },
  allClearText: { flex: 1, fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.medium, color: colors.text.primary },
  chipGrid: { flexWrap: 'wrap', gap: spacing.sm },
  attentionChip: {
    flexGrow: 1,
    flexBasis: 140,
    alignItems: 'center',
    gap: spacing.sm,
    backgroundColor: colors.background.surface,
    borderWidth: 1,
    borderRadius: designTokens.borderRadius.md,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
  },
  attentionChipWide: { flexBasis: 170 },
  attentionIcon: { width: 30, height: 30, borderRadius: 15, alignItems: 'center', justifyContent: 'center' },
  attentionCount: { fontSize: typography.fontSize.lg, fontFamily: typography.fontFamily.bold },
  attentionLabel: { flex: 1, fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.medium, color: colors.text.secondary },

  kpiGrid: { flexWrap: 'wrap', gap: spacing.sm },
  kpiTile: {
    flexGrow: 1,
    flexBasis: '45%',
    minWidth: 140,
    backgroundColor: colors.background.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    gap: 2,
  },
  kpiTileWide: { flexBasis: '30%' },
  kpiHead: { alignItems: 'flex-start', gap: 6 },
  kpiLabel: { flex: 1, fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.medium, color: colors.text.secondary },
  kpiValue: { fontSize: 24, fontFamily: typography.fontFamily.bold, color: colors.text.primary },
  kpiDetail: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.secondary },

  agendaRow: { alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm },
  agendaTime: { width: 64, fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  agendaIcon: { width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center' },
  agendaBody: { flex: 1, minWidth: 0 },
  agendaTitle: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  agendaSubtitle: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.secondary, marginTop: 1 },

  teamRow: { alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm },
  rowDivider: { borderTopWidth: 1, borderTopColor: colors.border },
  teamAvatar: { width: 30, height: 30, borderRadius: 15, backgroundColor: withAlpha(colors.primary, 0.12), alignItems: 'center', justifyContent: 'center' },
  teamInitial: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.bold, color: colors.primary },
  teamTalk: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.semibold, color: colors.text.secondary },

  shortcutGrid: { flexWrap: 'wrap', gap: spacing.sm },
  shortcut: {
    width: '22%',
    flexGrow: 1,
    minWidth: 72,
    alignItems: 'center',
    gap: 6,
    paddingVertical: spacing.sm,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.background.surface,
    borderWidth: 1,
    borderColor: colors.border,
  },
  // Six a row in the main column; no stretching of a short last row
  shortcutWide: { width: '15%', flexGrow: 0, minWidth: 88 },
  shortcutIcon: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center' },
  shortcutLabel: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.medium, color: colors.text.primary, textAlign: 'center', paddingHorizontal: 4 },
});
